"use client";

import { useEffect, useSyncExternalStore } from "react";

// Installing the app (F172). Chrome and Edge (Android, desktop) fire
// `beforeinstallprompt` once, early, on any page that links the manifest: the
// listener in the root layout keeps it, and «نصب» replays it later. iPhone
// Safari never fires it: there the card explains Share → Add to Home Screen.
// Nothing shows inside the installed app (display-mode standalone).

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/** Mounted once in the root layout: catches the install event before any page needs it. */
export function InstallPromptListener() {
  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      deferred = e as BeforeInstallPromptEvent;
      notify();
    };
    const onInstalled = () => {
      deferred = null;
      notify();
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);
  return null;
}

export type InstallMode = "prompt" | "ios" | "none";

/** Running as the installed app (home screen), not in a browser tab. */
export function isStandalone(): boolean {
  return window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** An iPhone or iPad, whichever browser (they all run on WebKit and install through Share). */
export function isIosDevice(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

/** Which iPhone browser shows the guide: Safari's Share button is at the bottom, Chrome's beside the address. */
export function iosBrowser(): "safari" | "chrome" | "other" {
  const ua = navigator.userAgent;
  if (/CriOS/i.test(ua)) return "chrome";
  if (/FxiOS|EdgiOS|OPiOS/i.test(ua)) return "other";
  return "safari";
}

function currentMode(): InstallMode {
  if (isStandalone()) return "none";
  if (deferred) return "prompt";
  return isIosDevice() ? "ios" : "none";
}

/** How this browser can install the app now: the replayable prompt, the iPhone guide, or not at all. */
export function useInstallMode(): InstallMode {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    currentMode,
    () => "none",
  );
}

/** Shows the browser's own install dialog (mode "prompt"). */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false;
  const event = deferred;
  await event.prompt();
  const { outcome } = await event.userChoice;
  deferred = null;
  notify();
  return outcome === "accepted";
}
